import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  Zap, User, CalendarDays, Clock, ChevronDown, Check, CheckCircle,
  Plus, X, Stethoscope, DoorOpen, Mail, Phone, StickyNote, ArrowRight,
} from 'lucide-react';
import {
  apiService, Appointment, Provider, Procedure, Operatory, resolveLocalized,
} from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import Celebration from './ui/Celebration';
import i18n from '../../i18n';

type Lang = 'de' | 'en' | 'es' | 'ru';

const LANGS: { code: Lang; label: string }[] = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
];

// Gängige Praxis-Zeiten als Ein-Tap-Auswahl. Native Zeitauswahl bleibt für
// Sonderfälle erhalten.
const TIME_CHIPS = ['09:00', '10:00', '11:00', '12:00', '16:00', '17:00'];

interface QuickAppointmentFormProps {
  /** 'page' = im Content-Fluss, 'modal' = als Overlay-Dialog. */
  variant?: 'page' | 'modal';
  /** Vorbelegtes Datum (z. B. aus Kalender-Klick). */
  initialDate?: string;
  /** Vorbelegte Startzeit HH:mm (z. B. aus Kalender-Slot-Klick). */
  initialTime?: string;
  /** Schließt die Maske (nur 'modal' / nach Abschluss). */
  onClose?: () => void;
  /** Nach erfolgreichem Anlegen — z. B. Liste neu laden. */
  onCreated?: (appointment: Appointment) => void;
}

// Default-Sprache der Maske. Eine zuvor bewusst gewählte Sprache (de/es/ru)
// wird respektiert; das technische i18n-Init-Default 'en' wird zu 'de'
// umgemünzt, weil die Praxis deutsch geführt ist und die übrige Admin-
// Navigation ebenfalls deutsch ist.
const initialLang = (): Lang => {
  const l = i18n.language?.slice(0, 2) as Lang;
  return l === 'de' || l === 'es' || l === 'ru' ? l : 'de';
};

const QuickAppointmentForm: React.FC<QuickAppointmentFormProps> = ({
  variant = 'page',
  initialDate,
  initialTime,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const nameRef = useRef<HTMLInputElement | null>(null);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [operatories, setOperatories] = useState<Operatory[]>([]);
  const [loadingDefaults, setLoadingDefaults] = useState(true);

  const [name, setName] = useState('');
  const [date, setDate] = useState(initialDate || moment().format('YYYY-MM-DD'));
  const [time, setTime] = useState(initialTime || '10:00');
  const [lang, setLang] = useState<Lang>(initialLang);

  const [providerId, setProviderId] = useState('');
  const [procedureId, setProcedureId] = useState('');
  const [operatoryId, setOperatoryId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<null | { name: string; date: string; time: string }>(null);
  const [celebrate, setCelebrate] = useState(false);

  // Defaults laden — jeweils erster aktiver Eintrag als Vorbelegung.
  useEffect(() => {
    (async () => {
      try {
        const [pv, pr, op] = await Promise.all([
          apiService.getProviders(true),
          apiService.getProcedures(true),
          apiService.getOperatories(true),
        ]);
        const pvList = pv.data.data || [];
        const prList = pr.data.data || [];
        const opList = op.data.data || [];
        setProviders(pvList);
        setProcedures(prList);
        setOperatories(opList);
        if (pvList[0]) setProviderId(String(pvList[0].id));
        if (prList[0]) setProcedureId(String(prList[0].id));
        if (opList[0]) setOperatoryId(String(opList[0].id));
      } catch {
        setError(t('common.error'));
      } finally {
        setLoadingDefaults(false);
      }
    })();
    // Maskensprache mit dem initialen Patientensprache-State synchronisieren,
    // damit die UI nicht im technischen i18n-Default 'en' erscheint.
    i18n.changeLanguage(lang);
    // Autofokus auf das Namensfeld — der einzige Pflicht-Tipp-Aufwand.
    setTimeout(() => nameRef.current?.focus(), 120);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedProcedure = useMemo(
    () => procedures.find((p) => String(p.id) === procedureId),
    [procedures, procedureId],
  );

  const duration = selectedProcedure?.duration ?? 30;
  const endTime = useMemo(
    () => moment(time, 'HH:mm').add(duration, 'minutes').format('HH:mm'),
    [time, duration],
  );

  // Sprachwahl steuert Maske (i18n) UND Patientensprache zugleich.
  const pickLang = (code: Lang) => {
    setLang(code);
    i18n.changeLanguage(code);
  };

  const dateChips = useMemo(
    () => [
      { key: 'today', label: t('quick.today'), value: moment().format('YYYY-MM-DD') },
      { key: 'tomorrow', label: t('quick.tomorrow'), value: moment().add(1, 'day').format('YYYY-MM-DD') },
      { key: 'dayAfter', label: t('quick.dayAfter'), value: moment().add(2, 'days').format('YYYY-MM-DD') },
    ],
    [t],
  );

  const resetForm = () => {
    setName('');
    setDate(initialDate || moment().format('YYYY-MM-DD'));
    setTime(initialTime || '10:00');
    setEmail('');
    setPhone('');
    setNotes('');
    setShowMore(false);
    setError(null);
    setSuccess(null);
    setTimeout(() => nameRef.current?.focus(), 120);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (name.trim().length < 2) {
      setError(t('quick.errMissingName'));
      nameRef.current?.focus();
      return;
    }
    if (!providerId || !procedureId || !operatoryId) {
      setError(t('quick.errNoDefaults'));
      return;
    }
    if (moment(date).isBefore(moment().startOf('day'))) {
      setError(t('quick.errPastDate'));
      return;
    }

    setSaving(true);
    try {
      const res = await apiService.createAppointment({
        patient_name: name.trim(),
        patient_email: email.trim(),
        patient_phone: phone.trim(),
        provider_id: parseInt(providerId),
        procedure_id: parseInt(procedureId),
        operatory_id: parseInt(operatoryId),
        appointment_date: date,
        start_time: time,
        end_time: endTime,
        notes: notes.trim(),
        language: lang,
        status: 'scheduled',
      });
      const created = res.data.data;
      setSuccess({ name: name.trim(), date: moment(date).format('DD.MM.YYYY'), time });
      setCelebrate(true);
      addToast(t('messages.appointmentCreated'), 'success');
      onCreated?.(created);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message || err?.response?.data?.error;
      setError(backendMsg || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  // ---- Erfolgs-State ---------------------------------------------------------
  if (success) {
    const body = (
      <div className="flex flex-col items-center text-center px-6 py-10">
        <div className="relative mb-5">
          <span className="absolute inset-0 rounded-full bg-emerald-400/30 blur-xl animate-pulse" aria-hidden />
          <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
            <CheckCircle className="h-11 w-11" strokeWidth={2} />
          </span>
        </div>
        <h2 className="text-xl font-bold text-brand-text">{t('quick.successTitle')}</h2>
        <p className="mt-2 text-base text-brand-text-muted">
          <span className="font-semibold text-brand-text">{success.name}</span>
          <br />
          {success.date} · {success.time}
        </p>

        <div className="mt-8 flex w-full max-w-xs flex-col gap-3">
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl bg-brand-primary px-6 text-base font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-brand-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50 cursor-pointer"
          >
            <Plus className="h-5 w-5" strokeWidth={2.25} />
            {t('quick.another')}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-xl border border-brand-border bg-brand-surface px-6 text-base font-semibold text-brand-text transition-colors duration-200 hover:bg-brand-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 cursor-pointer"
            >
              {t('quick.done')}
            </button>
          )}
        </div>
      </div>
    );

    return (
      <>
        <Celebration active={celebrate} onDone={() => setCelebrate(false)} />
        {variant === 'modal' ? (
          <ModalShell onClose={onClose}>{body}</ModalShell>
        ) : (
          <div className="mx-auto w-full max-w-lg rounded-2xl bg-brand-surface border border-brand-border shadow-sm">
            {body}
          </div>
        )}
      </>
    );
  }

  // ---- Formular --------------------------------------------------------------
  const inputBase =
    'w-full min-h-[56px] rounded-xl border border-brand-border bg-brand-surface px-4 text-base text-brand-text placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

  const form = (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <div className="px-5 pt-5 pb-3 sm:px-7 sm:pt-7">
        {/* Kopf */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary">
              <Zap className="h-6 w-6" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-brand-text leading-tight truncate">{t('quick.title')}</h1>
              <p className="text-[13px] text-brand-text-muted truncate">{t('quick.subtitle')}</p>
            </div>
          </div>
          {variant === 'modal' && onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="-mr-1 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-brand-text-muted hover:bg-brand-surface-2 hover:text-brand-text transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          )}
        </div>

        {error && (
          <div
            role="alert"
            className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800"
          >
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Patientenname */}
          <div>
            <label htmlFor="qa-name" className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-text">
              <User className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />
              {t('quick.patientName')}
            </label>
            <input
              id="qa-name"
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('quick.patientNamePlaceholder')}
              autoComplete="off"
              enterKeyHint="next"
              className={inputBase}
            />
          </div>

          {/* Datum */}
          <div>
            <label htmlFor="qa-date" className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-text">
              <CalendarDays className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />
              {t('quick.date')}
            </label>
            <div className="mb-2 grid grid-cols-3 gap-2">
              {dateChips.map((c) => {
                const active = date === c.value;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setDate(c.value)}
                    aria-pressed={active}
                    className={`min-h-[48px] rounded-xl border px-2 text-sm font-semibold transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
                      active
                        ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
                        : 'border-brand-border bg-brand-surface text-brand-text hover:bg-brand-surface-2'
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <input
              id="qa-date"
              type="date"
              value={date}
              min={moment().format('YYYY-MM-DD')}
              onChange={(e) => setDate(e.target.value)}
              className={inputBase}
            />
          </div>

          {/* Uhrzeit */}
          <div>
            <label htmlFor="qa-time" className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-text">
              <Clock className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />
              {t('quick.time')}
            </label>
            <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {TIME_CHIPS.map((c) => {
                const active = time === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setTime(c)}
                    aria-pressed={active}
                    className={`min-h-[48px] rounded-xl border px-2 text-sm font-semibold tabular-nums transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
                      active
                        ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
                        : 'border-brand-border bg-brand-surface text-brand-text hover:bg-brand-surface-2'
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <input
              id="qa-time"
              type="time"
              value={time}
              step={300}
              onChange={(e) => setTime(e.target.value)}
              className={`${inputBase} tabular-nums`}
            />
            <p className="mt-2 text-[13px] text-brand-text-muted">
              {t('quick.durationHint', { minutes: duration, end: endTime })}
            </p>
          </div>

          {/* Sprache */}
          <div>
            <span className="mb-2 block text-sm font-semibold text-brand-text">{t('quick.language')}</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {LANGS.map((l) => {
                const active = lang === l.code;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => pickLang(l.code)}
                    aria-pressed={active}
                    className={`flex min-h-[52px] items-center justify-center gap-2 rounded-xl border px-2 text-sm font-semibold transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
                      active
                        ? 'border-brand-primary bg-brand-primary/10 text-brand-primary'
                        : 'border-brand-border bg-brand-surface text-brand-text hover:bg-brand-surface-2'
                    }`}
                  >
                    {active && <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} />}
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mehr Optionen */}
          <div className="rounded-xl border border-brand-border bg-brand-surface-2/40">
            <button
              type="button"
              onClick={() => setShowMore((s) => !s)}
              aria-expanded={showMore}
              className="flex w-full min-h-[52px] items-center justify-between gap-2 px-4 text-sm font-semibold text-brand-text cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 rounded-xl"
            >
              <span>{showMore ? t('quick.lessOptions') : t('quick.moreOptions')}</span>
              <ChevronDown
                className={`h-5 w-5 text-brand-text-muted transition-transform duration-200 ${showMore ? 'rotate-180' : ''}`}
                strokeWidth={2}
              />
            </button>

            {showMore && (
              <div className="space-y-4 border-t border-brand-border px-4 py-4">
                <Field label={t('quick.provider')} icon={<Stethoscope className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />}>
                  <select value={providerId} onChange={(e) => setProviderId(e.target.value)} className={inputBase}>
                    {providers.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </Field>

                <Field label={t('quick.procedure')} icon={<Zap className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />}>
                  <select value={procedureId} onChange={(e) => setProcedureId(e.target.value)} className={inputBase}>
                    {procedures.map((p) => (
                      <option key={p.id} value={p.id}>
                        {resolveLocalized(p.name, lang)} ({p.duration} min)
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={t('quick.operatory')} icon={<DoorOpen className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />}>
                  <select value={operatoryId} onChange={(e) => setOperatoryId(e.target.value)} className={inputBase}>
                    {operatories.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </Field>

                <Field label={t('quick.email')} icon={<Mail className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />}>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="maria@example.com" className={inputBase} />
                </Field>

                <Field label={t('quick.phone')} icon={<Phone className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />}>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+34 612 345 678" className={inputBase} />
                </Field>

                <Field label={t('quick.notes')} icon={<StickyNote className="h-4 w-4 text-brand-text-muted" strokeWidth={2} />}>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    placeholder={t('quick.notesPlaceholder')}
                    className={`${inputBase} min-h-[88px] resize-none py-3`}
                  />
                </Field>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky-Aktionsleiste — daumenfreundlich am unteren Rand */}
      <div className="sticky bottom-0 mt-2 border-t border-brand-border bg-brand-surface/95 backdrop-blur-sm px-5 py-4 sm:px-7">
        <button
          type="submit"
          disabled={saving || loadingDefaults}
          className="inline-flex w-full min-h-[56px] items-center justify-center gap-2 rounded-xl bg-brand-primary px-6 text-base font-bold text-white shadow-sm transition-colors duration-200 hover:bg-brand-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-primary disabled:opacity-60 cursor-pointer"
        >
          {saving ? (
            <>
              <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('quick.saving')}
            </>
          ) : (
            <>
              {t('quick.save')}
              <ArrowRight className="h-5 w-5" strokeWidth={2.25} />
            </>
          )}
        </button>
      </div>
    </form>
  );

  if (variant === 'modal') {
    return <ModalShell onClose={onClose}>{form}</ModalShell>;
  }

  return (
    <div className="mx-auto w-full max-w-lg overflow-hidden rounded-2xl bg-brand-surface border border-brand-border shadow-sm">
      {form}
    </div>
  );
};

// Kleines Label+Icon-Feld für die "Mehr Optionen"-Sektion.
const Field: React.FC<{ label: string; icon: React.ReactNode; children: React.ReactNode }> = ({ label, icon, children }) => (
  <div>
    <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-text">
      {icon}
      {label}
    </label>
    {children}
  </div>
);

// Modal-Hülle: zentriert auf Desktop, als Bottom-Sheet-ähnliche Vollbreite auf
// Mobile. Scrollt intern bei langen Inhalten.
const ModalShell: React.FC<{ onClose?: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
    <div className="relative z-10 max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-brand-surface shadow-2xl sm:max-w-lg sm:rounded-2xl">
      {children}
    </div>
  </div>
);

export default QuickAppointmentForm;
